import { useState } from 'react';
import {
  familyMoments,
  familyRequest,
  familyToday,
  familyWeek,
  type ApiError,
  type FamilyMomentsResponse,
  type FamilyTodayResponse,
  type FamilyWeekResponse,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useQuery } from '../../useQuery';
import { sx } from '../../ui/designStyles';

/**
 * The top of the Family Portal's home in V9 (design fpEmShow and care-ops.js family()), under
 * the resident's name line:
 *
 *  - Emergency: while her emergency is open, "The care team has been alerted about Beatrice. A
 *    nurse will call you within 15 minutes."; once someone on staff has it, "A nurse is with
 *    Beatrice now. They will call you within 15 minutes." (GET /family/:rid/today, incident).
 *  - Day note, "Beatrice's day · yesterday": built from what staff charted yesterday
 *    (GET /family/:rid/week, yesterday's column), the care team's photos from yesterday
 *    (GET /family/:rid/moments), then "Ask a question", each a routed request
 *    (POST /family/:rid/requests, kind question:nurse | question:meals | question:bill).
 *
 * Honest by construction: a row shows only a charted value. What Aithora does not record
 * (sleep, activities) and what the week does not carry (mood) are named in one line, never
 * filled in. The design's per-question routing and answer times are not served, so the sent
 * line says what is true: the care team has it, with a day to answer.
 */

export interface DayNoteItem {
  k: string;
  v: string;
}

function possessive(pronouns: string | null | undefined): string {
  return pronouns === 'she' ? 'her' : pronouns === 'he' ? 'his' : 'their';
}

/** The note's rows, from yesterday's charted values; a row with nothing charted is left out. */
export function dayNoteItems(w: FamilyWeekResponse | null | undefined, pronouns?: string | null): { items: DayNoteItem[]; notCharted: string[] } {
  if (!w || !w.shared || w.days.length < 2) return { items: [], notCharted: [] };
  const y = w.days.length - 2;
  const items: DayNoteItem[] = [];
  const notCharted: string[] = [];
  for (const r of w.rows) {
    const v = r.values[y];
    const has = r.tracked && v !== null && v !== undefined && Number.isFinite(v);
    if (r.key === 'meals' && has) items.push({ k: 'Ate', v: `About ${Math.round(v as number)}% of ${possessive(pronouns)} meals, from what staff charted` });
    else if (r.key === 'water' && has) items.push({ k: 'Drank', v: `${Math.round(v as number).toLocaleString('en-US')} mL, from every drink staff logged` });
    else if (r.key === 'sleep' && has) items.push({ k: 'Slept', v: `About ${Math.round(v as number)} hours, from what staff charted` });
    else if (r.key === 'activities' && has) items.push({ k: 'Activity', v: `${Math.round(v as number)} ${Math.round(v as number) === 1 ? 'activity' : 'activities'}, from what staff charted` });
    else if (!r.tracked) notCharted.push(r.key === 'sleep' ? 'Sleep' : r.key === 'activities' ? 'Activities' : r.label);
  }
  // Mood is not part of the week: say so rather than leave it out silently.
  notCharted.push('Mood');
  return { items, notCharted };
}

/** "Sleep, activities and mood are not charted in Aithora yet." */
export function notChartedLine(names: string[]): string | null {
  if (!names.length) return null;
  const low = names.map((n, i) => (i === 0 ? n : n.toLowerCase()));
  const list = low.length === 1 ? low[0] : `${low.slice(0, -1).join(', ')} and ${low[low.length - 1]}`;
  return `${list} ${names.length === 1 ? 'is' : 'are'} not charted in Aithora yet.`;
}

/** The care team's photos from yesterday, in her building's time zone. */
export function yesterdaysPhotos(mo: FamilyMomentsResponse | null | undefined, w: FamilyWeekResponse | null | undefined): number {
  if (!mo || !w || w.days.length < 2) return 0;
  const day = w.days[w.days.length - 2].date;
  const tz = w.timezone || 'UTC';
  return mo.moments.filter((x) => {
    if (x.source !== 'staff' || x.mediaType !== 'photo' || !x.createdAt) return false;
    const d = new Date(x.createdAt);
    return !Number.isNaN(d.getTime()) && d.toLocaleDateString('en-CA', { timeZone: tz }) === day;
  }).length;
}

export function photoLine(n: number): string {
  return n === 0 ? 'No photo from the care team yesterday' : n === 1 ? '1 photo from the care team · in Moments' : `${n} photos from the care team · in Moments`;
}

/** The questions a family can route from the note (design care-ops.js Q). */
export const DAY_NOTE_ASKS = [
  { key: 'nurse', label: 'Ask the nurse', text: 'A question for the nurse about yesterday’s day note.' },
  { key: 'meals', label: 'Ask about meals', text: 'A question about meals from yesterday’s day note.' },
  { key: 'bill', label: 'Ask about the bill', text: 'A question about the bill.' },
] as const;

/**
 * The emergency line by the claim (V9 review round 2): unclaimed, the care team has been
 * alerted and a nurse will call; claimed, a nurse is with her. Closed: no banner.
 */
export function emergencyLine(inc: FamilyTodayResponse['incident'] | null | undefined, first: string): string | null {
  if (!inc) return null;
  if (inc.state === 'open') return `The care team has been alerted about ${first}. A nurse will call you within 15 minutes.`;
  if (inc.state === 'claimed') return `A nurse is with ${first} now. They will call you within 15 minutes.`;
  return null;
}

export function callLine(phone: string | null | undefined): string {
  return phone ? `You don't need to call. If you want to, the nurse station is ${phone}.` : "You don't need to call.";
}

const ASK =
  'display:inline-flex;align-items:center;min-height:40px;padding:0 12px;border-radius:8px;border:1px solid rgba(34,39,46,.18);color:#22272E;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;';

export function FpV9Top({ residentId, first, pronouns, readOnly, readOnlyLine }: { residentId: string; first: string; pronouns?: string | null; readOnly: boolean; readOnlyLine: string }) {
  const { client, session } = useSession();
  const deps = [client, session?.token, residentId];
  const today = useQuery((signal) => familyToday(client, { params: { residentId }, signal }), deps);
  const week = useQuery((signal) => familyWeek(client, { params: { residentId }, signal }), deps);
  const moments = useQuery((signal) => familyMoments(client, { params: { residentId }, signal }), deps);
  const [asked, setAsked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const em = emergencyLine(today.data?.incident, first);
  const w = week.data;
  const note = w && w.shared ? dayNoteItems(w, pronouns) : null;
  const gap = note ? notChartedLine(note.notCharted) : null;

  const ask = async (a: (typeof DAY_NOTE_ASKS)[number]) => {
    if (readOnly || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await familyRequest(client, { params: { residentId }, body: { kind: `question:${a.key}`, text: a.text } });
      setAsked(a.key);
    } catch (e) {
      setErr((e as ApiError)?.message ?? 'The question did not send. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!em && !note) return null;
  return (
    <>
      {em && (
        <div data-screen-label="Family Portal · Emergency" role="alert" data-testid="fp-emergency" style={sx('flex:0 0 auto;display:flex;flex-direction:column;gap:4px;margin-bottom:10px;padding:12px 14px;border-radius:12px;background:#FBEDEC;border:1px solid #B3261E;min-width:0;')}>
          <span style={sx('font-size:14px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#8A1C16;')}>{first}</span>
          <span data-testid="fp-emergency-line" style={sx('font-size:18px;font-weight:800;color:#8A1C16;line-height:1.35;')}>{em}</span>
          <span style={sx('font-size:16px;color:#3C454F;line-height:1.45;')}>{callLine(today.data?.nursePhone)}</span>
        </div>
      )}
      {note && (
        <div data-screen-label="Family day note" data-testid="fp-day-note" style={sx('flex:0 0 auto;display:flex;flex-direction:column;gap:6px;margin-bottom:10px;padding:12px 14px;border-radius:12px;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);min-width:0;')}>
          <span style={sx('font-size:14px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#2F5C97;')}>{`${first}’s day · yesterday`}</span>
          <span style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>Built from what the care team already charted. Nobody typed this for you.</span>
          {note.items.map((x) => (
            <div key={x.k} data-testid={'fp-day-note-' + x.k} style={sx('display:flex;align-items:baseline;gap:4px 12px;flex-wrap:wrap;padding:5px 0;border-top:1px solid rgba(34,39,46,.06);min-width:0;')}>
              <span style={sx('flex:0 1 300px;min-width:0;font-size:16px;font-weight:700;color:#22272E;')}>{x.k}</span>
              <span style={sx('flex:1 1 280px;min-width:0;font-size:16px;color:#22272E;line-height:1.4;')}>{x.v}</span>
            </div>
          ))}
          {!note.items.length && (
            <span data-testid="fp-day-note-empty" style={sx('padding:5px 0;border-top:1px solid rgba(34,39,46,.06);font-size:16px;color:#3C454F;line-height:1.4;')}>{`Nothing was charted for ${first} yesterday.`}</span>
          )}
          {gap && <span data-testid="fp-day-note-gap" style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>{gap}</span>}
          <div data-testid="fp-day-note-photo" style={sx('display:flex;align-items:center;justify-content:center;min-height:120px;border-radius:10px;border:1px dashed rgba(34,39,46,.28);font-size:14px;color:#54606C;text-align:center;padding:8px;')}>
            {photoLine(yesterdaysPhotos(moments.data, w))}
          </div>
          <span style={sx('font-size:16px;font-weight:800;color:#22272E;margin-top:2px;')}>Ask a question</span>
          {readOnly ? (
            <span data-testid="fp-day-note-ro" style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>{readOnlyLine}</span>
          ) : asked ? (
            <>
              <span data-testid="fp-day-note-asked" style={sx('font-size:16px;font-weight:700;color:#1F6B52;line-height:1.4;')}>Sent to the care team. It is on their list, with a day to answer.</span>
              <a href="#" data-testid="fp-day-note-again" onClick={(e) => { e.preventDefault(); setAsked(null); }} style={sx(ASK + 'align-self:flex-start;')}>
                Ask another question
              </a>
            </>
          ) : (
            <div style={sx('display:flex;gap:8px;flex-wrap:wrap;')}>
              {DAY_NOTE_ASKS.map((a) => (
                <a key={a.key} href="#" data-testid={'fp-day-note-ask-' + a.key} aria-disabled={busy || undefined} onClick={(e) => { e.preventDefault(); void ask(a); }} style={sx(ASK + (busy ? 'opacity:.6;' : ''))}>
                  {a.label}
                </a>
              ))}
            </div>
          )}
          {err && <span role="alert" style={sx('font-size:16px;font-weight:700;color:#B3261E;line-height:1.4;')}>{err}</span>}
        </div>
      )}
    </>
  );
}
